import { ItemStatus } from '../../../api/documentation/types/model'
import { DocumentSection } from '../../../entities/Document'
import { StatusIcon } from '../../../shared/ui/StatusIcon'
import styled from '@emotion/styled'
import React, { useState } from 'react'
import { colors } from '../../../shared/ui/colors'
import { Icon } from '../../../shared/ui/Icon'

interface TableRowProps {
  section: DocumentSection
  onDocumentationClick: (section: DocumentSection) => void
}

const DataRow = styled.tr``

const ToggleButton = styled.button<{ disabled?: boolean }>`
  display: ${({ disabled }) => (disabled ? 'none' : 'inline-flex')};
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: transparent;
  cursor: pointer;
  transition:
    background 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;

  &:hover {
    background: ${colors.white};
    box-shadow: 0 0 0 1px ${colors.gray300};
  }

  &:focus-visible {
    outline: 2px solid ${colors.orange500};
    outline-offset: 2px;
  }
`

const ChevronIcon = styled(Icon)<{ isExpanded: boolean }>`
  transition: transform 0.2s ease;
  transform: rotate(${({ isExpanded }) => (isExpanded ? '0deg' : '-90deg')});
`

const NumberCellContent = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`

const NumberCell = styled.td<{ level: number }>`
  border-bottom: 1px solid #e5e7eb;
  border-right: 1px solid #e5e7eb;
  border-left: 4px solid ${({ level }) => (level === 0 ? '#000000' : '#d1d5db')};
  padding: 8px 6px 8px 10px;
  height: 40px;
  vertical-align: middle;
  background-color: #f3f4f6;
  color: ${({ level }) => (level === 0 ? '#000000' : '#d1d5db')};
  font-weight: bold;
  text-align: center;
  cursor: pointer;
`

const ContentCell = styled.td`
  border-bottom: 1px solid #e5e7eb;
  border-right: 1px solid #e5e7eb;
  padding: 8px 10px;
  height: 40px;
  vertical-align: middle;
`

const StatusCell = styled.td`
  border-bottom: 1px solid #e5e7eb;
  border-right: 1px solid #e5e7eb;
  padding: 8px 10px;
  height: 40px;
  vertical-align: middle;
  text-align: center;
`

const StatusCellLast = styled.td`
  border-bottom: 1px solid #e5e7eb;
  padding: 8px 10px;
  height: 40px;
  vertical-align: middle;
  text-align: center;
`

const ContentWrapper = styled.div<{ level: number }>`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 100%;
  padding-left: ${({ level }) => level * 20}px;
`

const TextWrapper = styled.div`
  line-height: 1.4;
`

const CodeSpan = styled.span`
  background-color: #f3f4f6;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  margin-right: 6px;
`

const TitleSpan = styled.span`
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`

export const TableRow = ({ section, onDocumentationClick }: TableRowProps) => {
  const [collapsed, setCollapsed] = useState(true)
  const { level, subsections } = section
  const hadNestedRows = !!subsections.length

  const handleTitleClick = (
    evt: React.MouseEvent<HTMLSpanElement, MouseEvent>
  ) => {
    evt.stopPropagation()
    window.open(section.url, '_blank')
  }

  const handleDocumentationClick = () => {
    onDocumentationClick(section)
  }

  return (
    <React.Fragment key={section.id}>
      <DataRow>
        <NumberCell level={level} onClick={_ => setCollapsed(!collapsed)}>
          <NumberCellContent>
            {section.sectionNumber}
            {hadNestedRows && (
              <ToggleButton
                type="button"
                aria-expanded={!collapsed}
                aria-label={
                  !collapsed ? 'Свернуть строку' : 'Развернуть строку'
                }
              >
                <ChevronIcon
                  type="chevron_down"
                  size={16}
                  color={colors.gray600}
                  isExpanded={!collapsed}
                />
              </ToggleButton>
            )}
          </NumberCellContent>
        </NumberCell>
        <ContentCell>
          <ContentWrapper level={level}>
            {level > 0 && <span>↳</span>}
            <TextWrapper>
              <TitleSpan onClick={handleTitleClick}>
                {section.code && <CodeSpan>{section.code}</CodeSpan>}
                {section.title}
              </TitleSpan>
            </TextWrapper>
          </ContentWrapper>
        </ContentCell>
        <StatusCell>
          <StatusIcon
            status={section.stage === 'П' ? section.status : ItemStatus.NA}
            isCompact={true}
            variant="table"
            size={14}
            onClick={handleDocumentationClick}
          />
        </StatusCell>
        <StatusCellLast>
          <StatusIcon
            status={section.stage === 'РД' ? section.status : ItemStatus.NA}
            isCompact={true}
            variant="table"
            size={14}
            onClick={handleDocumentationClick}
          />
        </StatusCellLast>
      </DataRow>

      {!collapsed &&
        section.subsections.map(subsection => (
          <TableRow
            section={subsection}
            onDocumentationClick={onDocumentationClick}
          />
        ))}
    </React.Fragment>
  )
}
